import React from 'react';
import { CarTopSilhouette } from './CarTopSilhouette';
import { formatNumber } from './units';
import './cerebrumx.scss';

/** Front row above, rear row below; the car sits in the middle column. */
const WHEELS = [
	{ key: 'frontLeft', code: 'fl', labelKey: 'tireFL' },
	{ key: 'frontRight', code: 'fr', labelKey: 'tireFR' },
	{ key: 'rearLeft', code: 'rl', labelKey: 'tireRL' },
	{ key: 'rearRight', code: 'rr', labelKey: 'tireRR' },
];

/** The manufacturer's own verdict colors the status; only 'normal' is known to be good. */
const statusClass = (status) => {
	if (status.toLowerCase() === 'normal') return 'oem-tire-status oem-tire-status-ok';
	return 'oem-tire-status oem-tire-status-warn';
};

/** Stand-in for a payload without `cx_tires`: every tile reads N/A. */
const NO_TIRE = {};

const TireTile = ({ tire = NO_TIRE, code, labelKey, ctx }) => {
	const { t, locale } = ctx;
	const label = (key, values) => t(`oemDataModal_cerebrumx_diagnostics_${key}`, values);
	let reading = t('oemDataModal_notAvailable');
	if (tire.pressure !== undefined) reading = formatNumber(tire.pressure, locale);
	const hasFooter = tire.placard !== undefined || tire.status !== undefined;

	return (
		<div role="group" aria-label={label(labelKey)} className={`oem-tire-tile oem-tire-tile-${code}`}>
			<div className="oem-tire-tile-head">
				<span className="oem-tire-tile-code">{label(`wheels_${code}`)}</span>
				<span>{label('unit')}</span>
			</div>
			<p className="oem-tire-tile-reading">{reading}</p>
			{hasFooter && (
				<div className="oem-tire-tile-foot">
					<span>
						{tire.placard !== undefined
							&& label('placard', { value: formatNumber(tire.placard, locale) })}
					</span>
					{tire.status !== undefined && <span className={statusClass(tire.status)}>{tire.status}</span>}
				</div>
			)}
		</div>
	);
};

/**
 * TPMS-style layout: the four tires as tiles around a top-view silhouette,
 * each with the wheel code, the unit and the reading large, plus the placard
 * pressure and the manufacturer's status when served. Pressures are unitless
 * on the wire and rendered as psi (z1-ui-main's documented assumption),
 * whatever the user's unit preference. With `tires` undefined (the payload
 * carries no `cx_tires` block) the same layout is drawn greyed out, with a
 * "no tire data" note under it.
 */
export const TirePressureGrid = ({ tires, ctx }) => {
	const empty = tires === undefined;
	return (
		<div>
			<div className={empty ? 'oem-tire-grid oem-tire-grid-empty' : 'oem-tire-grid'}>
				{WHEELS.map(({ key, code, labelKey }) => (
					<TireTile key={key} tire={tires?.[key]} code={code} labelKey={labelKey} ctx={ctx} />
				))}
				<CarTopSilhouette className="oem-tire-grid-car" />
			</div>
			{empty && <p className="oem-tire-grid-note">{ctx.t('oemDataModal_cerebrumx_diagnostics_noTireData')}</p>}
		</div>
	);
};
